<script setup lang="ts">
import type { Component } from 'vue'
import AppIconChip from '../AppIconChip/AppIconChip.vue'
import AppPanel from '../AppPanel/AppPanel.vue'

withDefaults(
  defineProps<{
    /** `default` is the panel for a section with nothing yet · `lead` is the same panel as a band above a screen still rendering its scaffold · `compact` is the inline "nothing here" line inside a list. */
    size?: 'default' | 'compact' | 'lead'
    /** `default` and `lead`: the section's icon, a filled `@solar-icons/vue/bold` icon carried by a neutral icon chip. */
    icon?: Component
    title: string
    description?: string
  }>(),
  {
    size: 'default',
    icon: undefined,
    description: undefined,
  },
)

defineSlots<{
  /** `default`: one `md` primary `AppButton` · `lead`: up to two · `compact`: a link-weight action. */
  actions?: () => unknown
}>()
</script>

<template>
  <AppPanel
    v-if="size === 'default'"
    bare
    role="status"
    class="w-full flex-col items-center gap-12 h-fit p-24 text-center motion-safe:animate-fade-in"
  >
    <AppIconChip v-if="icon" :icon="icon" size="sm" />

    <div class="flex max-w-(--message-max-width) min-w-0 flex-col items-center gap-6">
      <h3 class="min-w-0 text-h3-medium text-text-primary">{{ title }}</h3>
      <p v-if="description" class="min-w-0 text-body-sm text-text-secondary">
        {{ description }}
      </p>
    </div>

    <div v-if="$slots.actions" class="flex flex-wrap items-center justify-center gap-6">
      <slot name="actions" />
    </div>
  </AppPanel>

  <AppPanel
    v-else-if="size === 'lead'"
    bare
    role="status"
    class="w-full flex-wrap items-center gap-6 h-fit p-8 motion-safe:animate-fade-in"
  >
    <AppIconChip v-if="icon" :icon="icon" size="sm" />
    <div class="flex min-w-0 flex-1 flex-col">
      <h3 class="min-w-0 text-h3-medium text-text-primary">{{ title }}</h3>
      <p v-if="description" class="min-w-0 text-body-sm text-text-secondary">
        {{ description }}
      </p>
    </div>
    <div v-if="$slots.actions" class="flex shrink-0 flex-wrap items-center gap-6">
      <slot name="actions" />
    </div>
  </AppPanel>

  <div
    v-else
    role="status"
    class="flex min-w-0 flex-col items-center gap-2 px-8 py-16 text-center motion-safe:animate-fade-in"
  >
    <p class="min-w-0 text-body-sm text-text-secondary">{{ title }}</p>
    <p v-if="description" class="min-w-0 text-caption text-text-tertiary">
      {{ description }}
    </p>
    <div v-if="$slots.actions" class="flex flex-wrap items-center justify-center gap-6">
      <slot name="actions" />
    </div>
  </div>
</template>
